import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Sofa, Truck, CheckCircle2, Phone, MapPin, RefreshCw, Camera, Flag } from "lucide-react";
import { toast } from "sonner";
import ImageLightbox from "@/components/site/ImageLightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = { awaiting_payment: "Booked · payment pending", scheduled: "Scheduled", in_progress: "Crew on the job", completed: "Delivered" };

function Row({ r, done, onPhoto, token, reasons, onChanged }) {
  return (
    <div data-testid={`portal-row-${r.ref_id}`} className="rounded-xl border-2 border-zinc-200 bg-white p-4 grid sm:grid-cols-[130px_1fr_auto] gap-3 items-start">
      <div>
        <div className="font-mono-data font-black text-[#0C2E2B] text-lg">{r.date}</div>
        <div className="text-xs text-zinc-500">{r.window}</div>
        <div className="text-[10px] font-mono-data text-zinc-400 mt-1">{r.ref_id}</div>
      </div>
      <div className="text-sm">
        <div className="font-bold text-zinc-900">{r.customer} <a href={`tel:${r.phone}`} className="ml-2 inline-flex items-center gap-1 text-xs text-[#0C2E2B] underline"><Phone className="w-3 h-3" /> {r.phone}</a></div>
        <div className="text-zinc-600 flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.address}</div>
        <div className="mt-1 flex flex-wrap gap-1">{r.items.map((it) => <span key={it} className="text-[11px] bg-[#FFDF00]/30 border border-[#FFDF00] rounded px-1.5 py-0.5">{it}</span>)}</div>
        {r.details && <div className="text-xs text-zinc-500 mt-1 italic">"{r.details}"</div>}
      </div>
      <div className="text-right text-xs">
        <span className={`inline-block font-bold uppercase tracking-widest px-2 py-1 rounded ${done ? "bg-emerald-100 text-emerald-800" : r.paid ? "bg-[#0C2E2B] text-[#FFDF00]" : "bg-zinc-100 text-zinc-600"}`}>{STATUS[r.status] || r.status}</span>
        {done && r.completed_at && <div className="text-zinc-500 mt-1">{new Date(r.completed_at).toLocaleDateString()}{r.signed_by ? ` · signed by ${r.signed_by}` : ""}</div>}
        {!done && <FlagControl r={r} token={token} reasons={reasons} onChanged={onChanged} />}
      </div>
      {!done && r.flag && (
        <div className="sm:col-span-3 flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 rounded-md px-3 py-2 text-xs" data-testid={`portal-flagged-${r.ref_id}`}>
          <Flag className="w-3.5 h-3.5" /> <b>Flagged not ready</b> · {r.flag.reason_label}{r.flag.note ? ` — ${r.flag.note}` : ""} · Harvey's crew has been alerted.
        </div>
      )}
      {done && r.receipt && (r.receipt.photo_urls.length > 0 || r.receipt.signature_url) && (
        <div className="sm:col-span-3 border-t border-zinc-100 pt-3" data-testid={`portal-receipt-${r.ref_id}`}>
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2"><Camera className="w-3 h-3" /> Proof of delivery{r.receipt.signed_at ? ` · ${new Date(r.receipt.signed_at).toLocaleString()}` : ""}</div>
          <div className="flex flex-wrap gap-2">
            {r.receipt.photo_urls.map((u, i) => (
              <button key={u} type="button" data-testid={`portal-receipt-photo-${r.ref_id}-${i}`} onClick={() => onPhoto({ urls: r.receipt.photo_urls, index: i })} className="w-24 h-24 rounded-md overflow-hidden border border-zinc-200 hover:ring-2 ring-[#FFDF00]">
                <img src={u} alt={`Setup photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
            {r.receipt.signature_url && (
              <div className="h-24 px-3 rounded-md border border-dashed border-zinc-300 bg-white flex flex-col items-center justify-center" data-testid={`portal-receipt-signature-${r.ref_id}`}>
                <img src={r.receipt.signature_url} alt="Customer signature" className="h-12 object-contain" />
                <span className="text-[10px] text-zinc-500 mt-1">Customer signature</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FlagControl({ r, token, reasons, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("not_arrived");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/store-portal/${token}/flag/${r.ref_id}`, { reason, note });
      toast.success("Harvey's Place has been alerted — crew will hold");
      setOpen(false); setNote(""); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/store-portal/${token}/flag/${r.ref_id}`); toast.success("Marked ready — thanks!"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); } finally { setBusy(false); }
  };
  if (r.flag) return <button type="button" data-testid={`portal-ready-${r.ref_id}`} disabled={busy} onClick={clear} className="mt-2 text-[11px] font-black uppercase tracking-wider bg-emerald-600 text-white px-2.5 py-1.5 rounded hover:bg-emerald-700">Order is ready now</button>;
  if (!open) return <button type="button" data-testid={`portal-flag-${r.ref_id}`} onClick={() => setOpen(true)} className="mt-2 inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider border-2 border-red-500 text-red-700 px-2.5 py-1 rounded hover:bg-red-50"><Flag className="w-3 h-3" /> Not ready</button>;
  return (
    <div className="mt-2 text-left bg-white border border-zinc-200 rounded-md p-2 space-y-2 w-64" data-testid={`portal-flag-form-${r.ref_id}`}>
      <select data-testid={`portal-flag-reason-${r.ref_id}`} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-zinc-300 rounded px-2 py-1 text-xs bg-white">
        {Object.entries(reasons || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <input data-testid={`portal-flag-note-${r.ref_id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the crew (optional)" className="w-full border border-zinc-300 rounded px-2 py-1 text-xs" />
      <div className="flex gap-2">
        <button type="button" data-testid={`portal-flag-send-${r.ref_id}`} disabled={busy} onClick={submit} className="flex-1 text-[11px] font-black uppercase bg-red-600 text-white px-2 py-1.5 rounded">{busy ? "Sending…" : "Alert crew"}</button>
        <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-zinc-500 px-2">Cancel</button>
      </div>
    </div>
  );
}

export default function StorePortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const load = () => axios.get(`${API}/store-portal/${token}`).then(({ data }) => { setData(data); setErr(""); }).catch((e) => setErr(e?.response?.data?.detail || "Could not load"));
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center text-zinc-600" data-testid="portal-error">{err}</div>;
  if (!data) return <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center text-zinc-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="store-portal-page">
      <header className="bg-[#0C2E2B] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-tactical text-[11px] uppercase tracking-[0.3em] text-[#FFDF00] flex items-center gap-2"><Sofa className="w-4 h-4" /> Harvey's Place · Partner portal</div>
            <h1 className="font-display font-black text-3xl sm:text-4xl mt-2" data-testid="portal-store-name">{data.store.name}</h1>
            <p className="text-white/70 text-sm mt-1">{data.store.address} · Flat rate ${Number(data.store.flat_rate).toFixed(0)} within {data.included_miles} mi</p>
          </div>
          <button onClick={load} data-testid="portal-refresh" className="inline-flex items-center gap-1 text-xs font-bold bg-white/10 hover:bg-white/20 px-3 py-2 rounded-md"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-10">
        <section>
          <h2 className="font-display font-black text-xl text-[#0C2E2B] flex items-center gap-2 mb-3"><Truck className="w-5 h-5" /> Upcoming pickups <span className="text-zinc-400 text-base">· {data.upcoming.length}</span></h2>
          <div className="space-y-3" data-testid="portal-upcoming">
            {data.upcoming.length === 0 ? <p className="text-sm text-zinc-500" data-testid="portal-upcoming-empty">Nothing scheduled yet.</p> : data.upcoming.map((r) => <Row key={r.ref_id} r={r} token={token} reasons={data.flag_reasons} onChanged={load} />)}
          </div>
        </section>
        <section>
          <h2 className="font-display font-black text-xl text-[#0C2E2B] flex items-center gap-2 mb-3"><CheckCircle2 className="w-5 h-5" /> Completed deliveries <span className="text-zinc-400 text-base">· {data.completed.length}</span></h2>
          <div className="space-y-3" data-testid="portal-completed">
            {data.completed.length === 0 ? <p className="text-sm text-zinc-500">No completed deliveries yet.</p> : data.completed.map((r) => <Row key={r.ref_id} r={r} done onPhoto={setLightbox} />)}
          </div>
        </section>
        <p className="text-[11px] text-zinc-400">Private view for {data.store.name}. Use "Not ready" to hold the crew if an order hasn't arrived. Keep this link private. Questions: reply to any Harvey's Place email.</p>
      </main>
      <ImageLightbox images={lightbox?.urls || []} startIndex={lightbox?.index || 0} open={!!lightbox} onClose={() => setLightbox(null)} title="Proof of delivery" />
    </div>
  );
}
